import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Image as ImageIcon, Play } from 'lucide-react';
import { fetchSessions } from '../services/supabaseService';
import { useConferenceYear } from '../contexts/ConferenceYearContext';
import { useAccessibility } from '../contexts/AccessibilityContext';
import { useListNavigation } from '../hooks/useListNavigation';
import AacButton from '../components/aac/AacButton';
import PosterPresentation from '../components/PosterPresentation';
import './Posters.css';

const Posters = () => {
  const navigate = useNavigate();
  const { activeYear } = useConferenceYear();
  const { announce, focusFirstHeading } = useAccessibility();
  const [posters, setPosters] = useState([]);
  const [loading, setLoading] = useState(true);
  const [presentationStartIndex, setPresentationStartIndex] = useState(null);

  useEffect(() => {
    focusFirstHeading();
    announce('Posters page loaded');
  }, [announce, focusFirstHeading]);

  useEffect(() => {
    const loadPosters = async () => {
      try {
        setLoading(true);
        const sessions = await fetchSessions(activeYear);
        setPosters(sessions.filter((s) => s.format === 'Poster'));
      } catch (err) {
        console.error('❌ Error loading posters:', err);
        setPosters([]);
      } finally {
        setLoading(false);
      }
    };

    loadPosters();
  }, [activeYear]);

  const { currentItem: currentNavPoster } = useListNavigation(posters, {
    onOpen: (poster) => navigate(`/session/${poster.sessionNumber || poster.id}`),
    getLabel: (poster) => (poster.sessionNumber ? `${poster.sessionNumber}: ${poster.title}` : poster.title),
    getId: (poster) => poster.id,
  });
  const currentNavPosterId = currentNavPoster ? currentNavPoster.id : null;

  const posterRefs = useRef(new Map());
  useEffect(() => {
    if (!currentNavPosterId) return;
    const el = posterRefs.current.get(currentNavPosterId);
    if (!el) return;
    el.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    el.focus({ preventScroll: true });
  }, [currentNavPosterId]);

  // Only posters with an uploaded PDF/image can be shown in presentation
  // mode — everything else still appears in the plain list below with its
  // number and title, same as before any images existed.
  const postersWithImages = posters.filter((poster) => poster.posterImageUrl);

  const openPresentation = (poster) => {
    const startIndex = poster
      ? postersWithImages.findIndex((p) => p.id === poster.id)
      : 0;
    setPresentationStartIndex(startIndex >= 0 ? startIndex : 0);
  };

  const closePresentation = (lastViewedPoster) => {
    setPresentationStartIndex(null);
    if (!lastViewedPoster) return;
    // useModalDialog restores focus to whatever opened the dialog as part
    // of its own cleanup — wait a frame so this lands after that, not
    // before it, and put focus on the poster actually being viewed when
    // it closed (which after paging with Next/Previous is usually not the
    // one originally opened).
    requestAnimationFrame(() => {
      const el = posterRefs.current.get(lastViewedPoster.id);
      if (!el) return;
      el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      el.focus({ preventScroll: true });
      announce(`Back to poster list, ${lastViewedPoster.title}`);
    });
  };

  if (loading) {
    return (
      <div className="posters-page">
        <div className="posters-center">
          <p>Loading posters…</p>
        </div>
      </div>
    );
  }

  return (
    <div className="posters-page">
      <header className="posters-header">
        <h1>Posters</h1>
        <p className="posters-subtitle">
          {posters.length} poster{posters.length === 1 ? '' : 's'} this year
        </p>
        {postersWithImages.length > 0 && (
          <AacButton onClick={() => openPresentation(null)} shortcut={1}>
            <Play size={18} aria-hidden="true" /> Start Presentation
          </AacButton>
        )}
      </header>

      <p className="posters-notice">
        {postersWithImages.length === 0
          ? 'Poster images will be added closer to the conference — the list below shows presenter and title details only for now.'
          : `${postersWithImages.length} of ${posters.length} posters have images available. Click "Start Presentation" or a poster's "View Poster" button to browse them full-screen, or press N/P to move through the list below.`}
      </p>

      {posters.length === 0 ? (
        <p className="posters-empty">No posters have been published yet. Check back soon.</p>
      ) : (
        <ul className="posters-list">
          {posters.map((poster) => {
            const isCurrent = currentNavPosterId === poster.id;
            return (
              <li
                key={poster.id}
                ref={(el) => {
                  if (el) posterRefs.current.set(poster.id, el);
                  else posterRefs.current.delete(poster.id);
                }}
                tabIndex={0}
                className={`poster-card ${isCurrent ? 'list-nav-current' : ''}`}
              >
                <div className="poster-card-thumb" aria-hidden="true">
                  {poster.sessionNumber ? (
                    <span className="poster-card-number">{poster.sessionNumber}</span>
                  ) : (
                    <ImageIcon size={22} />
                  )}
                </div>
                <div className="poster-card-body">
                  <h2 className="poster-card-title">
                    <Link to={`/session/${poster.sessionNumber || poster.id}`}>
                      {poster.title}
                    </Link>
                  </h2>
                  <p className="poster-card-speaker">{poster.speaker || 'Speaker TBA'}</p>
                  {poster.track && <span className="poster-card-track">{poster.track}</span>}
                </div>
                {poster.posterImageUrl && (
                  <button
                    type="button"
                    className="btn btn-outline poster-card-view-btn"
                    onClick={() => openPresentation(poster)}
                  >
                    View Poster
                  </button>
                )}
              </li>
            );
          })}
        </ul>
      )}

      {presentationStartIndex !== null && (
        <PosterPresentation
          posters={postersWithImages}
          startIndex={presentationStartIndex}
          onClose={closePresentation}
        />
      )}
    </div>
  );
};

export default Posters;
